import React, { useState, useEffect } from "react";
import {
  Box,
  Button,
  Card,
  CardContent,
  CircularProgress,
  Stack,
  TextField,
  Typography,
  Alert,
  Avatar,
  Chip,
  Divider,
} from "@mui/material";
import {
  ArrowBack as ArrowBackIcon,
  Edit as EditIcon,
  CreditCard as CreditCardIcon,
} from "@mui/icons-material";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../../contexts/AuthContext";
import { supabase } from "../../lib/supabase";
import { ContractorLayout } from "../../components/layout/ContractorLayout";
import { useSubscription } from "../../hooks/useSubscription";

interface IContractorProfile {
  id: string;
  email: string;
  name: string;
  phone?: string;
  business_name?: string;
  role: "contractor";
  created_at: string;
}

export function ContractorProfile() {
  const navigate = useNavigate();
  const { profile: authProfile } = useAuth();

  const [profile, setProfile] = useState<IContractorProfile | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [editing, setEditing] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);

  const [formData, setFormData] = useState({
    name: "",
    phone: "",
    business_name: "",
  });

  const subscription = useSubscription();
  const [portalLoading, setPortalLoading] = useState(false);
  const [cancelLoading, setCancelLoading] = useState(false);
  const [cancelError, setCancelError] = useState<string | null>(null);

  const statusColors: Record<string, "success" | "warning" | "error" | "default"> = {
    active: "success",
    trialing: "success",
    past_due: "warning",
    unpaid: "error",
    canceled: "default",
    paused: "default",
    incomplete: "warning",
    incomplete_expired: "error",
  };

  const handleManageBilling = async () => {
    setPortalLoading(true);
    await subscription.openBillingPortal();
    setPortalLoading(false);
  };

  const handleCancelSubscription = async () => {
    setCancelError(null);
    setCancelLoading(true);
    const result = await subscription.cancelSubscription();
    setCancelLoading(false);
    if ("error" in result) {
      setCancelError(result.error);
    }
  };

  useEffect(() => {
    if (authProfile) {
      setProfile({
        id: authProfile.id,
        email: authProfile.email || "",
        name: authProfile.name || "",
        phone: authProfile.phone || "",
        business_name: authProfile.business_name || "",
        role: authProfile.role as "contractor",
        created_at: authProfile.created_at || new Date().toISOString(),
      });

      setFormData({
        name: authProfile.name || "",
        phone: authProfile.phone || "",
        business_name: authProfile.business_name || "",
      });

      setLoading(false);
    }
  }, [authProfile]);

  const handleInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = event.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleSave = async () => {
    if (!profile) return;

    setSaving(true);
    setError(null);
    setSuccess(false);

    try {
      const { error: updateError } = await supabase
        .from("users")
        .update({
          name: formData.name,
          phone: formData.phone || null,
          business_name: formData.business_name || null,
        })
        .eq("id", profile.id);

      if (updateError) throw updateError;

      // Update local state
      setProfile((prev) =>
        prev
          ? {
              ...prev,
              name: formData.name,
              phone: formData.phone,
              business_name: formData.business_name,
            }
          : null,
      );

      setSuccess(true);
      setEditing(false);

      // Clear success message after 3 seconds
      setTimeout(() => setSuccess(false), 3000);
    } catch (err) {
      const message =
        err instanceof Error ? err.message : "Failed to update profile";
      setError(message);
    } finally {
      setSaving(false);
    }
  };

  const handleCancel = () => {
    if (profile) {
      setFormData({
        name: profile.name,
        phone: profile.phone || "",
        business_name: profile.business_name || "",
      });
    }
    setEditing(false);
  };

  if (loading) {
    return (
      <ContractorLayout>
        <Box
          sx={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            minHeight: "60vh",
          }}
        >
          <CircularProgress />
        </Box>
      </ContractorLayout>
    );
  }

  if (!profile) {
    return (
      <ContractorLayout>
        <Box sx={{ p: 3 }}>
          <Button
            startIcon={<ArrowBackIcon />}
            onClick={() => navigate("/contractor")}
            sx={{ mb: 2 }}
          >
            Back
          </Button>
          <Alert severity="error">Failed to load profile</Alert>
        </Box>
      </ContractorLayout>
    );
  }

  return (
    <ContractorLayout>
      <Stack spacing={3}>
        <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
          <Button
            startIcon={<ArrowBackIcon />}
            onClick={() => navigate("/contractor")}
          >
            Back to Dashboard
          </Button>
          <Box sx={{ flex: 1 }} />
          {!editing && (
            <Button
              variant="contained"
              startIcon={<EditIcon />}
              onClick={() => setEditing(true)}
            >
              Edit Profile
            </Button>
          )}
        </Box>

        {/* Subscription Card */}
        <Card>
          <CardContent sx={{ p: 4 }}>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 3 }}>
              <CreditCardIcon color="primary" />
              <Typography variant="h5" sx={{ fontWeight: 700 }}>
                Subscription
              </Typography>
            </Box>

            {cancelError && <Alert severity="error" sx={{ mb: 2 }}>{cancelError}</Alert>}
            {subscription.cancelAtPeriodEnd && (
              <Alert severity="info" sx={{ mb: 2 }}>
                Your subscription will cancel at the end of the billing period. You'll keep full access until then.
              </Alert>
            )}

            {subscription.loading ? (
              <CircularProgress size={24} />
            ) : (
              <Stack spacing={2}>
                <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                  <Typography variant="body2" color="text.secondary">Status</Typography>
                  <Chip
                    label={subscription.status ?? "No subscription"}
                    size="small"
                    color={statusColors[subscription.status ?? ""] ?? "default"}
                  />
                </Box>

                {subscription.periodEnd && (
                  <Box>
                    <Typography variant="body2" color="text.secondary">
                      {subscription.status === "trialing"
                        ? "Trial ends"
                        : subscription.cancelAtPeriodEnd
                          ? "Cancels on"
                          : "Next billing date"}:{" "}
                      <strong>{subscription.periodEnd.toLocaleDateString()}</strong>
                    </Typography>
                  </Box>
                )}

                <Divider />

                <Stack direction="row" spacing={2}>
                  <Button
                    variant="outlined"
                    startIcon={portalLoading ? <CircularProgress size={16} /> : <CreditCardIcon />}
                    onClick={handleManageBilling}
                    disabled={portalLoading}
                  >
                    {portalLoading ? "Opening…" : "Manage billing"}
                  </Button>
                  {subscription.isActive && !subscription.cancelAtPeriodEnd && (
                    <Button
                      variant="text"
                      color="error"
                      onClick={handleCancelSubscription}
                      disabled={cancelLoading}
                    >
                      {cancelLoading ? "Canceling…" : "Cancel subscription"}
                    </Button>
                  )}
                </Stack>
              </Stack>
            )}
          </CardContent>
        </Card>

        <Card>
          <CardContent sx={{ p: 4 }}>
            <Typography variant="h5" sx={{ fontWeight: 700, mb: 3 }}>
              Profile Information
            </Typography>

            {error && (
              <Alert severity="error" sx={{ mb: 3 }}>
                {error}
              </Alert>
            )}
            {success && (
              <Alert severity="success" sx={{ mb: 3 }}>
                Profile updated successfully!
              </Alert>
            )}

            {/* Profile Header */}
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 3,
                mb: 4,
                pb: 3,
                borderBottom: 1,
                borderColor: "divider",
              }}
            >
              <Avatar
                sx={{
                  width: 80,
                  height: 80,
                  bgcolor: "primary.main",
                  fontSize: "2rem",
                }}
              >
                {profile.name.charAt(0).toUpperCase()}
              </Avatar>
              <Box>
                <Typography variant="h6" sx={{ fontWeight: 600 }}>
                  {profile.name}
                </Typography>
                <Typography
                  variant="body2"
                  color="text.secondary"
                  sx={{ mt: 0.5 }}
                >
                  {profile.email}
                </Typography>
                <Chip
                  label="Contractor"
                  size="small"
                  color="primary"
                  variant="outlined"
                  sx={{ mt: 1 }}
                />
              </Box>
            </Box>

            {/* Form */}
            <Stack spacing={3}>
              <Box>
                <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1 }}>
                  Name
                </Typography>
                <TextField
                  fullWidth
                  value={formData.name}
                  onChange={handleInputChange}
                  name="name"
                  disabled={!editing}
                  variant={editing ? "outlined" : "standard"}
                />
              </Box>

              <Box>
                <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1 }}>
                  Email
                </Typography>
                <TextField
                  fullWidth
                  value={profile.email}
                  disabled
                  variant="standard"
                  helperText="Email cannot be changed"
                />
              </Box>

              <Box>
                <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1 }}>
                  Business Name
                </Typography>
                <TextField
                  fullWidth
                  value={formData.business_name}
                  onChange={handleInputChange}
                  name="business_name"
                  disabled={!editing}
                  placeholder="Your business name"
                  variant={editing ? "outlined" : "standard"}
                />
              </Box>

              <Box>
                <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1 }}>
                  Phone Number
                </Typography>
                <TextField
                  fullWidth
                  value={formData.phone}
                  onChange={handleInputChange}
                  name="phone"
                  disabled={!editing}
                  placeholder="(555) 123-4567"
                  variant={editing ? "outlined" : "standard"}
                />
              </Box>

              {/* Member Since */}
              <Box>
                <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1 }}>
                  Member Since
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {new Date(profile.created_at).toLocaleDateString("en-US", {
                    year: "numeric",
                    month: "long",
                    day: "numeric",
                  })}
                </Typography>
              </Box>

              <Divider />

              {/* Action Buttons */}
              {editing && (
                <Stack
                  direction="row"
                  spacing={2}
                  sx={{ justifyContent: "flex-end" }}
                >
                  <Button onClick={handleCancel} disabled={saving}>
                    Cancel
                  </Button>
                  <Button
                    variant="contained"
                    onClick={handleSave}
                    disabled={saving}
                  >
                    {saving ? "Saving..." : "Save Changes"}
                  </Button>
                </Stack>
              )}
            </Stack>
          </CardContent>
        </Card>
      </Stack>
    </ContractorLayout>
  );
}
